import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import AutoLoginPage from './pages/AutoLoginPage';
import OverviewPage from './pages/OverviewPage';
import HomePage from './pages/HomePage';
import BusinessHealthPage from './pages/BusinessHealthPage';import ExecutiveDashboard from './pages/ExecutiveDashboard';
import HiringDecisionPage from './pages/HiringDecisionPage';
import AIAnalysisPage from './pages/AIAnalysisPage';
import RevenuePage from './pages/RevenuePage';
import CashflowPage from './pages/CashflowPage';
import ExpensesPage from './pages/ExpensesPage';
import HutangPage from './pages/HutangPage';
import ProfitLossPage from './pages/ProfitLossPage';
import BalanceSheetPage from './pages/BalanceSheetPage';
import MonthlySummaryPage from './pages/MonthlySummaryPage';
import StaffPayrollPage from './pages/StaffPayrollPage';
import StaffProfile360Page from './pages/StaffProfile360Page';
import EInvoicePage from './pages/EInvoicePage';
import EInvoiceFormPage from './pages/EInvoiceFormPage';
import AuditLogsPage from './pages/AuditLogsPage';
import WingmanActivityPage from './pages/WingmanActivityPage';
import LHDNFormsPage from './pages/LHDNFormsPage';
import AssetPage from './pages/AssetPage';
import HRPage from './pages/HRPage';
import CommandCenterPage from './pages/CommandCenterPage';
import ChannelProfitabilityPage from './pages/ClientProfitabilityPage';
import StaffAnalyticsPage from './pages/StaffAnalyticsPage';
import StaffDetailsPage from './pages/StaffDetailsPage';
import StaffFolderPage from './pages/StaffFolderPage';
import GuidePage from './pages/GuidePage';
import BoardReportPage from './pages/BoardReportPage';
import BenchmarkingPage from './pages/BenchmarkingPage';
import LoanPage from './pages/LoanPage';
import SQLAccountingPage from './pages/SQLAccountingPage';
import CustomerAnalyticsPage from './pages/CustomerAnalyticsPage';
import InventoryAnalyticsPage from './pages/InventoryAnalyticsPage';
import SecurityAuditPage from './pages/SecurityAuditPage';
import SelfServicePortalPage from './pages/SelfServicePortalPage';
import ExpenseClaimsQueuePage from './pages/ExpenseClaimsQueuePage';
import TeamCalendarPage from './pages/TeamCalendarPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import LatenessPage from './pages/LatenessPage';
import SalaryAdvanceQueuePage from './pages/SalaryAdvanceQueuePage';
import OrgChartPage from './pages/OrgChartPage';
import '@/App.css';

// Role-based home page redirect
const HomeRedirect = () => {
  const { userRole } = useApp();

  // Staff users go to self-service portal
  if (userRole === 'staff') {
    return <Navigate to="/me" replace />;
  }

  // HR users go directly to HR page (they don't see Finance dashboards)
  if (userRole === 'hr') {
    return <Navigate to="/hr" replace />;
  }

  // Director / Finance see the consolidated HomePage (Quick Snapshot / Overview / Executive / Command Center)
  return <HomePage />;
};

// AppRouter handles auth callback detection synchronously
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment BEFORE rendering routes
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auto-login" element={<AutoLoginPage />} />

      {/* Self-Service Portal — chrome-less, native PWA-style. Staff role primarily. */}
      <Route path="/me" element={
        <ProtectedRoute>
          <div className="min-h-[100dvh] portal-pwa-shell">
            <SelfServicePortalPage />
            <PWAInstallPrompt />
          </div>
        </ProtectedRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/*" element={
        <ProtectedRoute>
          <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar - Always visible on desktop */}
            <Sidebar />
            
            {/* Main content area */}
            <div className="flex-1 lg:ml-64 min-w-0">
              <Header />
              <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
                <Routes>
                {/* Home - redirects based on role */}
                <Route path="/" element={<HomeRedirect />} />
                
                {/* Panduan - accessible by all */}
                <Route path="/panduan" element={<GuidePage />} />
                
                {/* HR Routes - accessible by HR and Finance */}
                <Route path="/hr" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <HRPage />
                  </RoleBasedRoute>
                } />
                <Route path="/staff-payroll" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <StaffPayrollPage />
                  </RoleBasedRoute>
                } />
                <Route path="/staff/:staffId" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <StaffProfile360Page />
                  </RoleBasedRoute>
                } />
                <Route path="/einvoice" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <EInvoicePage />
                  </RoleBasedRoute>
                } />
                <Route path="/einvoice/:invoiceId" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <EInvoiceFormPage />
                  </RoleBasedRoute>
                } />
                <Route path="/audit-logs" element={
                  <RoleBasedRoute allowedRoles={['director']} redirectTo="/">
                    <AuditLogsPage />
                  </RoleBasedRoute>
                } />
                <Route path="/wingman-activity" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance', 'director']}>
                    <WingmanActivityPage />
                  </RoleBasedRoute>
                } />
                <Route path="/lhdn-forms" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <LHDNFormsPage />
                  </RoleBasedRoute>
                } />
                <Route path="/staff-analytics" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <StaffAnalyticsPage />
                  </RoleBasedRoute>
                } />
                <Route path="/staff-details" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <StaffDetailsPage />
                  </RoleBasedRoute>
                } />
                <Route path="/staff-folder" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <StaffFolderPage />
                  </RoleBasedRoute>
                } />
                <Route path="/hiring" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <HiringDecisionPage />
                  </RoleBasedRoute>
                } />
                <Route path="/expense-claims" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <ExpenseClaimsQueuePage />
                  </RoleBasedRoute>
                } />
                <Route path="/team-calendar" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <TeamCalendarPage />
                  </RoleBasedRoute>
                } />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/lateness" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <LatenessPage />
                  </RoleBasedRoute>
                } />
                <Route path="/salary-advances" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <SalaryAdvanceQueuePage />
                  </RoleBasedRoute>
                } />
                <Route path="/org-chart" element={
                  <RoleBasedRoute allowedRoles={['hr', 'finance']}>
                    <OrgChartPage />
                  </RoleBasedRoute>
                } />
                
                {/* Finance-only Routes */}
                <Route path="/executive" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <ExecutiveDashboard />
                  </RoleBasedRoute>
                } />
                <Route path="/ai-analysis" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <AIAnalysisPage />
                  </RoleBasedRoute>
                } />
                <Route path="/revenue" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <RevenuePage />
                  </RoleBasedRoute>
                } />
                <Route path="/cashflow" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <CashflowPage />
                  </RoleBasedRoute>
                } />
                <Route path="/expenses" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <ExpensesPage />
                  </RoleBasedRoute>
                } />
                <Route path="/hutang" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <HutangPage />
                  </RoleBasedRoute>
                } />
                <Route path="/business-health" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <BusinessHealthPage />
                  </RoleBasedRoute>
                } />
                <Route path="/profit-loss" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <ProfitLossPage />
                  </RoleBasedRoute>
                } />
                <Route path="/balance-sheet" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <BalanceSheetPage />
                  </RoleBasedRoute>
                } />
                <Route path="/monthly-summary" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <MonthlySummaryPage />
                  </RoleBasedRoute>
                } />
                <Route path="/assets" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <AssetPage />
                  </RoleBasedRoute>
                } />
                <Route path="/command-center" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <CommandCenterPage />
                  </RoleBasedRoute>
                } />
                <Route path="/channel-profitability" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <ChannelProfitabilityPage />
                  </RoleBasedRoute>
                } />
                <Route path="/board-report" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <BoardReportPage />
                  </RoleBasedRoute>
                } />
                <Route path="/benchmarking" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <BenchmarkingPage />
                  </RoleBasedRoute>
                } />
                <Route path="/loans" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <LoanPage />
                  </RoleBasedRoute>
                } />
                <Route path="/sql-accounting" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <SQLAccountingPage />
                  </RoleBasedRoute>
                } />
                <Route path="/customer-analytics" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <CustomerAnalyticsPage />
                  </RoleBasedRoute>
                } />
                <Route path="/inventory-analytics" element={
                  <RoleBasedRoute allowedRoles={['finance']} redirectTo="/hr">
                    <InventoryAnalyticsPage />
                  </RoleBasedRoute>
                } />
                
                {/* Director-only Routes */}
                <Route path="/security-audit" element={
                  <RoleBasedRoute allowedRoles={['director']} redirectTo="/">
                    <SecurityAuditPage />
                  </RoleBasedRoute>
                } />
              </Routes>
            </main>
            <Footer />
            </div>
          </div>
          <PWAInstallPrompt />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppRouter />
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
